import type {
  RawImpedanceResult,
  RawImpedancePolar,
  ImpedancePoint,
  ChannelData,
  MeasurementData,
  DeltaChannelData,
} from './types';
import type { ImpedanceResult } from '@/lib/protobuf';

/**
 * Converts a protobuf ImpedanceResult (per-channel `data[]`, each with its own
 * `magnitudeOhm`/`phaseRad` arrays sharing `frequencyHz`) to the canonical stored
 * format — a flat, frequency-major interleaved `values[]` array (`values[freqIdx *
 * channelCount + chIdx]`), the same layout `deinterleaveChannels` expects when
 * reading historical/stored results back out. Use this before sending live
 * measurement data to the backend API.
 */
export function toRawImpedanceResult(result: ImpedanceResult): RawImpedanceResult {
  const safeNum = (n: number): number => (Number.isFinite(n) ? n : 0);
  const frequencies = result.frequencyHz ?? [];
  const channels = result.data ?? [];

  const values: RawImpedancePolar[] = [];
  frequencies.forEach((_freq, freqIdx) => {
    channels.forEach((channel) => {
      values.push({
        magnitude_Ohm: safeNum(channel.magnitudeOhm[freqIdx]),
        phase_rad: safeNum(channel.phaseRad[freqIdx]),
      });
    });
  });

  return {
    frequency_Hz: frequencies.map(safeNum),
    values,
  };
}

export function polarToCartesian(
  magnitude: number,
  phase: number,
): { real: number; imag: number } {
  return {
    real: magnitude * Math.cos(phase),
    imag: magnitude * Math.sin(-phase),
  };
}

export function radToDeg(radians: number): number {
  return radians * (180 / Math.PI);
}

export function createImpedancePoint(
  frequency: number,
  magnitude: number,
  phase: number,
): ImpedancePoint {
  const { real, imag } = polarToCartesian(magnitude, phase);
  return {
    frequency,
    magnitude,
    phase,
    phaseDeg: radToDeg(phase),
    zReal: real,
    zImag: imag,
  };
}

/**
 * De-interleaves the flat values array from an ImpedanceResult into per-channel data.
 *
 * Interleave convention (from device):
 *   values[freqIdx * channelCount + channelIdx]
 *
 * e.g. 4 channels × 25 frequencies = 100 values:
 *   index 0  = CH1 @ freq[0]
 *   index 1  = CH2 @ freq[0]
 *   index 2  = CH3 @ freq[0]
 *   index 3  = CH4 @ freq[0]
 *   index 4  = CH1 @ freq[1]
 *   ...
 */
export function deinterleaveChannels(
  raw: RawImpedanceResult,
  channelIds: string[],
): MeasurementData {
  const channelCount = channelIds.length;
  const frequencies = raw.frequency_Hz;
  const expectedLength = frequencies.length * channelCount;

  if (raw.values.length !== expectedLength) {
    throw new Error(
      `ImpedanceResult values length mismatch: expected ${expectedLength} ` +
        `(${frequencies.length} frequencies × ${channelCount} channels) ` +
        `but got ${raw.values.length}.`,
    );
  }

  const channels: ChannelData[] = channelIds.map((id, chIdx) => ({
    channelId: id,
    channelIndex: chIdx,
    points: frequencies.map((freq, freqIdx) => {
      const valueIndex = freqIdx * channelCount + chIdx;
      const polar = raw.values[valueIndex];
      return createImpedancePoint(freq, polar.magnitude_Ohm, polar.phase_rad);
    }),
  }));

  return { channels, frequencies, channelCount };
}

/**
 * Computes per-channel, per-frequency delta (specimen minus baseline).
 * Delta coordinates are derived from delta magnitude/phase, preserving the polar relationship.
 */
export function computeDelta(
  baseline: MeasurementData,
  specimen: MeasurementData,
): DeltaChannelData[] {
  return baseline.channels.map((blCh, chIdx) => {
    const spCh = specimen.channels[chIdx];
    return {
      channelId: blCh.channelId,
      channelIndex: chIdx,
      points: blCh.points.map((blPt, ptIdx) => {
        const spPt = spCh.points[ptIdx];
        const dMag = spPt.magnitude - blPt.magnitude;
        const dPhase = spPt.phase - blPt.phase;
        return createImpedancePoint(blPt.frequency, dMag, dPhase);
      }),
    };
  });
}
